<template>
  <v-card flat>
    <VideoPlayerQuizModal />
    <v-row>
      <v-col cols="12">
        <v-btn color="primary" icon @click="handleBack">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle">Quiz</span>
        <helper-tooltip :click="handleKnowMore" />
      </v-col>
      <v-col cols="12" class="pt-0">
        <span class="ml-3 mb-3 text-body-1 textType2--text">{{
          $t('message.ai.quiz.quizDescription')
        }}</span>
      </v-col>
      <div :class="{ 'd-none': isLoading }">
        <v-col cols="12" class="d-flex flex-row">
          <v-switch
            v-model="isEnabledInPlayer"
            @click="toggleActivation"
            :disabled="preventToggleQuickly"
            class="my-0 py-0 ml-3 align-center"
            compact
            hide-details
          ></v-switch
          ><span class="ml-1 pt-2 text-body-1 textType--text">{{
            $t('message.ai.quiz.enableQuiz')
          }}</span>
        </v-col>
      </div>
      <v-col cols="12" v-if="!isLoading">
        <v-btn
          class="ml-4"
          small
          outlined
          color="primary"
          @click="handleEditModal"
        >
          {{ $t('message.actions.edit') }}
        </v-btn>
      </v-col>
    </v-row>
    <template v-if="isLoading">
      <v-container>
        <v-row class="d-flex mt-2">
          <v-col cols="12">
            <v-skeleton-loader
              class="border loader-width"
              type="chip"
            ></v-skeleton-loader>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader
              class="border loader-width"
              type="image"
            ></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-else-if="!isEnabledInPlayer">
      <div></div>
    </template>
    <v-row no-gutters v-else-if="!editMode">
      <div class="scroll-container ml-3 mt-8">
        <div v-for="(question, index) in questions" :key="index">
          <p class="mb-0 enunciate">
            {{ $t('message.ai.quiz.question') }}{{ index + 1 }}{{ ')' }}
            {{ question.enunciate }}
          </p>
          <ul class="no-list-style">
            <li
              v-for="(option, optionIndex) in question.options"
              :key="optionIndex"
            >
              <span :class="{ 'right-answer': option.correct }"
                >{{ String.fromCharCode(97 + optionIndex) }}{{ ')' }}
                {{ option.text }}.</span
              >
            </li>
            <br />
          </ul>
        </div>
      </div>
    </v-row>
    <template v-else>
      <v-col cols="12" class="pa-0 mt-5 pl-3 mb-4">
        <v-icon
          v-if="isEditing"
          class="mr-2"
          style="cursor: pointer"
          @click="cancelEditing"
          >mdi-chevron-left</v-icon
        >
        <v-btn
          v-if="isEditing"
          :loading="loadings.updatingQuestions"
          color="primary"
          @click="handleSaveQuestion"
          >{{ $t('message.ai.quiz.saveQuestion') }}</v-btn
        >
        <v-btn v-else color="primary" @click="handleQuestion">{{
          $t('message.ai.quiz.addQuestion')
        }}</v-btn>
      </v-col>
      <v-col cols="12" v-if="isEditing">
        <v-textarea-legacy
          v-model="beingEditedQuestion.enunciate"
          outlined
          label="Enunciado"
          hide-details
          rows="2"
          row-height="20"
        ></v-textarea-legacy>
        <v-col cols="12" class="pa-0 mt-5">
          <v-row
            v-for="(option, index) in beingEditedQuestion.options"
            :key="index"
            no-gutters
            class="mt-2"
          >
            <v-col class="shrink">
              <v-radio-group
                v-model="questionCorrectIndex"
                hide-details
                class="mt-0"
              >
                <v-radio class="mt-1" :value="index"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col>
              <v-text-field
                v-model="option.text"
                outlined
                hide-details
                label="Opção"
                dense
              ></v-text-field>
            </v-col>
            <v-col
              class="pl-2 shrink d-flex flex-row align-center justify-center"
            >
              <v-icon
                :disabled="beingEditedQuestion.options.length < 2"
                style="cursor: pointer"
                @click="removeOption(index)"
              >
                mdi-delete
              </v-icon>
            </v-col>
          </v-row>
          <v-btn
            :disabled="beingEditedQuestion.options.length > 4"
            class="mt-5"
            color="primary"
            outlined
            @click="addOption"
            >{{ $t('message.ai.quiz.addOption') }}</v-btn
          >
        </v-col>
      </v-col>
      <v-col
        v-else
        cols="12"
        v-for="(question, index) in questions"
        :key="index"
        class="py-1"
      >
        <v-card class="mt-2 pl-2 mr-6" outlined height="45px">
          <v-row
            class="fill-height"
            no-gutters
            align="center"
            justify="space-between"
          >
            <span
              class="text-truncate"
              style="max-width: calc(95% - 50px)"
              :title="question.enunciate"
              >{{ question.enunciate }}</span
            >
            <v-progress-circular
              v-if="
                loadings.removingQuestion &&
                loadings.removingQuestionIndex === index
              "
              class="mr-2"
              indeterminate
              color="primary"
              size="22"
              width="3"
            ></v-progress-circular>
            <v-menu v-else bottom left offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="mr-2" icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list dense class="pa-0" width="120px">
                <v-list-item @click="handleQuestion(question, index)">
                  <v-list-item-title>{{
                    $t('message.pages.videoPlayerButtons.edit')
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeQuestion(index)">
                  <v-list-item-title class="error--text">{{
                    $t('message.pages.videoEditModal.delete')
                  }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>
        </v-card>
      </v-col>
    </template>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import playerApi from '@/services/playerApi.js'
import videosApi from '@/services/videosApi.js'
import linkHelper from '@/utils/linkHelper.mixin.js'
import HelperTooltip from '@/components/HelperTooltip.vue'
import aiworkflowApi from '@/services/aiworkflowApi'
import VideoPlayerQuizModal from './VideoPlayerQuizModal.vue'

export default {
  name: 'VideoPlayerQuiz',
  mixins: [linkHelper],
  components: { HelperTooltip, VideoPlayerQuizModal },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      isEnabledInPlayer: false,
      preventToggleQuickly: false,
      questions: '',
      videoConfig: {},
      initialEditingQuestionValue: null,
      beingEditedQuestion: null,
      question: { enunciate: '', options: [{ text: '', corrrect: true }] },
      isEditing: false,
      questionCorrectIndex: 0,
      questionIndex: 0,
      loadings: {
        updatingQuestions: false,
        removingQuestion: false,
        removingQuestionIndex: 0,
      },
      isUpdateQuestion: false,
      editMode: false,
    }
  },
  created() {
    this.checkEnabledItems()
  },
  mounted() {
    this.handleData()
    this.$root.$on('quizUpdateQuestions', (questions) => {
      this.questions = questions
    })
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      hasData: (state) => state.video.aiWorkFlow?.data,
    }),
  },
  methods: {
    ...mapActions('video', ['aiWorkFlowStatus', 'handleFetchAiWorkFlowData']),
    handleEditModal() {
      this.$root.$emit('openVideoQuizModal', { questions: this.questions })
    },
    cancelEditing() {
      this.isEditing = false
      if (this.isUpdateQuestion) {
        this.questions[this.questionIndex] = this.initialEditingQuestionValue
      }
    },
    async removeQuestion(indexToRemove) {
      this.loadings.removingQuestion = true
      this.loadings.removingQuestionIndex = indexToRemove
      const newArray = [
        ...this.questions.slice(0, indexToRemove),
        ...this.questions.slice(indexToRemove + 1),
      ]
      await this.updateQuestions(newArray)
      this.loadings.removingQuestion = false
      this.loadings.removingQuestionIndex = 0
    },
    addOption() {
      if (this.beingEditedQuestion.options.length > 4) return
      this.beingEditedQuestion.options.push({ text: '', correct: false })
    },
    removeOption(optionIndex) {
      if (this.beingEditedQuestion.options.length < 2) return
      if (this.beingEditedQuestion.options[optionIndex].correct) {
        this.questionCorrectIndex = 0
      }
      this.beingEditedQuestion.options.splice(optionIndex, 1)
    },
    handleQuestion(question, index) {
      this.isUpdateQuestion = false
      this.beingEditedQuestion = {
        enunciate: '',
        options: [{ text: '', corrrect: true }],
      }
      if (question?.enunciate) {
        this.initialEditingQuestionValue = question
        this.beingEditedQuestion = question
        this.questionIndex = index
        this.isUpdateQuestion = true
      }
      this.isEditing = true
    },
    async handleSaveQuestion() {
      if (
        this.questionIndex >= 0 &&
        this.questionIndex < this.questions.length &&
        this.isUpdateQuestion
      ) {
        this.questions[this.questionIndex] = this.beingEditedQuestion
      }
      let newQuestions = this.questions.map((question) => question)
      if (!this.isUpdateQuestion) {
        newQuestions.push(this.beingEditedQuestion)
      }
      this.loadings.updatingQuestions = true
      await this.updateQuestions(newQuestions)
      this.isEditing = false
      this.loadings.updatingQuestions = false
    },
    async updateQuestions(newQuestions) {
      const videoId = this.$route.params.id
      const aiData = {
        video_id: videoId,
        questions: newQuestions,
      }
      try {
        let res = await aiworkflowApi.updateAiWorkFlowData(aiData)
        this.questions = res.data.questions
      } catch (e) {
        console.error('e', e)
      }
    },
    handleKnowMore() {
      const link = this.getQuizLink()
      window.open(link, '_blank')
    },
    async handleData() {
      if (this.hasData) {
        const { questions } = this.hasData
        this.questions = questions
      } else {
        let { pullzone_name, video_external_id } = this.video
        try {
          const { questions } = await this.handleFetchAiWorkFlowData({
            pullzone_name,
            video_external_id,
          })

          this.questions = questions
        } catch (error) {
          console.error(error)
        }
      }
    },
    async toggleActivation() {
      this.preventToggleQuickly = true
      this.videoConfig['questions'] = this.isEnabledInPlayer
      await playerApi.updateAiVideoConfig(this.video.id, this.videoConfig)
      this.preventToggleQuickly = false
    },
    async checkEnabledItems() {
      try {
        const {
          data: {
            config: { ai: videoConfig },
          },
        } = await videosApi.getAVideo(this.video.id)
        this.isEnabledInPlayer = (videoConfig || {}).questions || false
        this.videoConfig = videoConfig
        this.isLoading = false
      } catch (error) {
        console.log(error)
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.right-answer {
  color: #1b5e20;
  background-color: #c8e6c9;
}

.no-list-style {
  list-style-type: none;
  padding-left: 0;
  font-weight: 400;
}

.enunciate {
  font-weight: 500;
}

.scroll-container {
  max-height: 500px;
  overflow-y: auto;
}
</style>
